<template>
  <v-dialog
    :model-value="modelValue"
    width="900"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card elevation="0">
      <v-card-title class="pa-0">
        <v-toolbar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>
            <span class="mdi mdi-rocket-launch-outline" />
            {{ $t('dashboard.next-emails.dialog-title') }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              class="pointer"
              @click="closeDialog"
            >
              <v-icon size="large"> mdi-close </v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>

      <div class="table-wrapper">
        <v-table
          ref="tableRef"
          fixed-header
          height="600"
          class="text-no-wrap"
        >
          <template v-if="!loadingNextEmails">
            <thead class="table-header">
              <tr :style="{ color: $vuetify.theme.global?.current?.colors.primary }">
                <th class="text-left">
                  {{ $t('dashboard.next-emails.table-header.0') }}
                </th>
                <th class="text-left col-wide">
                  {{ $t('dashboard.next-emails.table-header.1') }}
                </th>

                <th class="text-left">
                  {{ $t('dashboard.next-emails.table-header.3') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="localNextEmail in localNextEmails"
                :key="localNextEmail?.id"
                class="clickable"
                @click="$emit('openPageDetails', localNextEmail.id)"
              >
                <td
                  :style="{ color: $vuetify.theme.global?.current?.colors.primary }"
                  class="text-decoration-underline"
                >
                  <span v-if="localNextEmail && localNextEmail?.firstName">
                    {{ localNextEmail.firstName ?? '' }} {{ localNextEmail?.lastName ?? '' }}
                  </span>
                  <span v-else>{{ localNextEmail?.email ?? '' }}</span>
                </td>
                <td class="col-wide">
                  <div class="d-flex flex-wrap ga-2 py-2">
                    <div
                      v-for="{ type, label, color } of CONVERSION_TYPES"
                      :key="type"
                    >
                      <v-chip
                        v-if="showAction(localNextEmail, type)"
                        :color="
                          isPropertyRequestUrgent(localNextEmail, type) ? 'red-lighten-4' : color
                        "
                        :key="type"
                        size="x-small"
                        variant="flat"
                        rounded
                      >
                        {{ $t(label) }}
                      </v-chip>
                    </div>
                  </div>
                </td>
                <td>{{ formatDate(localNextEmail.scheduledAt) }}</td>
              </tr>
              <tr v-if="!localNextEmails?.length">
                <td
                  colspan="4"
                  class="text-center py-6 text-medium-emphasis"
                >
                  {{ $t('dashboard.next-emails.no-next-emails') }}
                </td>
              </tr>
            </tbody>
          </template>
          <v-skeleton-loader
            v-else
            :style="{ width: $vuetify.display.mdAndUp ? '82%' : '100%' }"
            class="mx-auto loader-skeleton"
            type="article"
          />
        </v-table>
      </div>

      <v-card-actions>
        <v-btn
          class="d-flex justify-end"
          color="primary"
          @click="closeDialog"
          :disabled="loadingNextEmails"
        >
          {{ $t('dashboard.next-emails.close-dialog') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { usePropertyRequest } from '@/composables/usePropertyRequest'
import { useShowAction } from '@/composables/useShowAction'
import { formatDate } from '@/lib/dateTime'
import { CONVERSION_TYPES } from '@/modules/leads/configs/conversionTypes'

const { showAction } = useShowAction()
const { isPropertyRequestUrgent } = usePropertyRequest()
const tableRef = ref()
const loading = ref(false)
const pendingLoad = ref(false)
const disbaleLoadMore = ref(false)
const lastTriggeredPage = ref(0)
const localNextEmails = ref<NextEmail[]>([])
const page = ref(1)
let scrollEl: HTMLElement | null = null

type NextEmail = {
  id: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  conversionCountByType: Record<string, number>
  scheduledAt: Date
  actions: string[]
}

const props = defineProps<{
  limit: number
  loadingNextEmails: boolean
  modelValue: boolean
  nextEmails: NextEmail[]
}>()

onMounted(async () => {
  await nextTick()

  const wrapper = tableRef.value?.$el.querySelector('.v-table__wrapper')

  if (wrapper) {
    wrapper.addEventListener('scroll', handleScroll)
  }
})

const handleScroll = async (e: Event) => {
  const el = e.target as HTMLElement

  const isBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20

  if (!isBottom) {
    pendingLoad.value = false
    return
  }

  if (loading.value || pendingLoad.value || disbaleLoadMore.value) return

  pendingLoad.value = true
  loading.value = true

  try {
    page.value += 1
    lastTriggeredPage.value = page.value
    await emitLoadMore(page.value)
  } finally {
    loading.value = false
  }
}

const emitLoadMore = async (pageNumber: number) => {
  emit('loadMore', pageNumber)
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'openPageDetails', id: string): void
  (e: 'loadMore', page: number): void
}>()

const closeDialog = () => {
  emit('update:modelValue', false)
}

const attachScrollListener = async () => {
  await nextTick()

  detachScrollListener()

  scrollEl = tableRef.value?.$el?.querySelector('.v-table__wrapper') ?? null

  if (scrollEl) {
    scrollEl.addEventListener('scroll', handleScroll)
  }
}

const detachScrollListener = () => {
  if (scrollEl) {
    scrollEl.removeEventListener('scroll', handleScroll)
    scrollEl = null
  }
}

watch(
  () => props.modelValue,
  async (isOpen) => {
    if (isOpen) {
      await attachScrollListener()
    } else {
      detachScrollListener()
    }
  },
  { immediate: true }
)

watch(
  () => props.nextEmails,
  (newValue) => {
    if (!Array.isArray(newValue) || !newValue.length) return
    if (newValue.length < page.value * props.limit) {
      disbaleLoadMore.value = true
    }
    localNextEmails.value = [...newValue]
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  detachScrollListener()
})
</script>
<style scoped>
.table-header {
  background-color: #eef4f4;
}
.clickable {
  cursor: pointer;
}

.table-wrapper table {
  border-collapse: separate;
  border-spacing: 0;
}

.table-wrapper thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.col-wide {
  width: 400px;
  min-width: 350px;
}
.loader-skeleton {
  height: 600px;
}
</style>
